/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  IconMenu2,
  IconX,
  IconGridDots,
  IconBell,
  IconSearch,
  IconUser,
  IconBuilding,
  IconSettings,
  IconLogout,
  IconChevronRight,
  IconHistory,
  IconPencil,
  IconBasketCog,
  IconClipboardList,
  IconPackage,
  IconHeadset,
  IconPackages,
  IconUsersGroup,
  IconTruck,
  IconFileAnalytics,
  IconFileInvoice,
  IconMapSearch,
  IconChevronDown,
  IconCoins,
  IconUserCheck,
  IconFileText,
  IconDeviceLaptop,
  IconTrendingUp,
  IconLanguage,
} from "@tabler/icons-react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetch3, fetch5 } from "../commonApis/commonApis";
import { useLanguage, T } from "../context/LanguageContext";
import {
  useProfileData,
  useUpdateProfilePicMutation,
  useClientPermissions,
} from "../apis/profileApis";
import toast from "react-hot-toast";
import { useFetchAdminNotificationsData } from "../apis/notificationApis";
import { useSelector } from "react-redux";
import { usePermission } from "../hooks/usePermissionTabs";
import { useQueryClient } from "@tanstack/react-query";
import { io } from "socket.io-client";
import { BulkResponseHandler } from "./BulkResponseHandler";
import { NotificationItem } from "./NotificaionItem";

interface SearchResultItem {
  entityType: "user" | "order" | "client";
  entityId: string;
  displayTitle: string;
  subText?: string;
  awbNumber?: string;
  orderName?: string;
  referenceNumber?: string;
  status?: string;
  createdAt?: string;
}

/**
 * modules: string[] — any ONE of these modules needs a 'read' permission
 * for the nav tab to be shown. Omit modules (or leave empty) to always show.
 */
const navItems = [
  {
    name: "Order",
    path: "/orders",
    icon: <IconPackage />,
    modules: ["orders"],
  },
  {
    name: "Users",
    path: "/users",
    icon: <IconUsersGroup />,
    modules: [
      "staffs",
      "clients",
      "drivers",
      "out_source",
      "access_center",
      "client_request",
    ],
  },
  {
    name: "Fleet",
    path: "/fleet-management",
    icon: <IconTruck />,
    modules: ["vehicles", "line_haul", "line_haul_request"],
  },
  {
    name: "Manifest",
    path: "/manifest-management",
    icon: <IconMapSearch />,
    modules: ["pickup_manifests", "linehaul_manifests", "return_to_shipper"],
  },
  {
    name: "Inventory",
    path: "/inventory-management",
    icon: <IconPackages />,
    modules: ["product_inventory", "shelves", "products", "shipment_inventory"],
  },
  {
    name: "DRS",
    path: "/drs-management",
    icon: <IconFileAnalytics />,
    modules: ["drs"],
  },
  {
    name: "Invoice",
    path: "/invoice-management",
    icon: <IconFileInvoice />,
    modules: ["invoices", "linehaul_invoices"],
  },
  // {
  //   name: "Document",
  //   path: "/documents",
  //   icon: <IconFileInvoice />,
  //   modules: [],
  // },
  {
    name: "Reports",
    path: "/reports",
    icon: <IconMapSearch />,
    modules: ["reports"],
  },
  {
    name: "Outlets",
    path: "/outlet-management",
    icon: <IconBuilding />,
    modules: ["outlet"],
  },
  {
    name: "Integration",
    path: "/integration",
    icon: <IconBasketCog />,
    modules: ["integration"],
  },
  { name: "Dark Store", path: "/dark-store", icon: <IconMapSearch /> },
  // Newly integrated HRMS Tabs
  { name: "Company", path: "/company", icon: <IconBuilding />, isHRMS: true },
  { name: "HR", path: "/hr", icon: <IconUsersGroup />, isHRMS: true },
  { name: "Recruitment", path: "/recruitment", icon: <IconUserCheck />, isHRMS: true },
  { name: "Payroll", path: "/payroll", icon: <IconCoins />, isHRMS: true },
  { name: "Letter", path: "/letter", icon: <IconFileText />, isHRMS: true },
  { name: "Noticeboard", path: "/noticeboard", icon: <IconBell />, isHRMS: true },
  { name: "Asset", path: "/asset", icon: <IconDeviceLaptop />, isHRMS: true },
  { name: "Performance", path: "/performance", icon: <IconTrendingUp />, isHRMS: true },
];

const getEntityPath = (type: string, id: string): string => {
  switch (type) {
    case "order":
      return `/orders/${id}`;
    case "user":
      return `/users/${id}`;
    case "client":
      return `/clients/${id}`;
    default:
      return "/orders";
  }
};

const useDebounce = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

const getCookieVal = (name: string) => {
  const val = `; ${document.cookie}`;
  const parts = val.split(`; ${name}=`);
  if (parts.length >= 2) return parts.pop()?.split(";").shift();
};

export default function Header() {
  const queryClient = useQueryClient();
  const profile = useSelector((state: any) => state.auth.profile);
  const permissions = useSelector((state: any) => state.auth.permissions);
  const navigate = useNavigate();
  const location = useLocation();

  const [activeAppModule, setActiveAppModule] = useState<string>(() => {
    return localStorage.getItem("activeAppModule") || "Logistic";
  });

  const hasHRMSAccess = useMemo(() => {
    const isSuperAdmin = profile?.type === "superAdmin" || profile?.role === "superAdmin";
    if (isSuperAdmin) return true;

    // For clients and client staff, HR access is strictly determined by hrOption === "HRMS"
    const isClientOrStaff = profile?.type === "client" || profile?.type === "lmStaff";
    if (isClientOrStaff) {
      return profile?.hrOption === "HRMS";
    }

    return (
      profile?.hrOption === "HRMS" ||
      profile?.clientType === "HRMS" ||
      permissions?.some((p: any) => 
        ["HRMS", "HR", "Payroll", "Recruitment", "Company", "Letter", "Asset", "Performance", "Noticeboard"].includes(p?.module) ||
        p?.module?.toLowerCase()?.includes("hr")
      )
    );
  }, [profile, permissions]);

  const hasLogisticAccess = useMemo(() => {
    const isSuperAdmin = profile?.type === "superAdmin" || profile?.role === "superAdmin";
    if (isSuperAdmin) return true;
    return (
      profile?.clientType === "BOTH" ||
      profile?.clientType === "ALL" ||
      profile?.clientType === "LM" ||
      profile?.clientType === "FM" ||
      !profile?.clientType
    );
  }, [profile]);

  // Sync activeAppModule with the route path
  useEffect(() => {
    const path = location.pathname;
    const hrmsPaths = ["/company", "/hr", "/recruitment", "/payroll", "/letter", "/noticeboard", "/asset", "/performance"];
    
    if (hrmsPaths.some(p => path.startsWith(p))) {
      if (activeAppModule !== "HRMS" && hasHRMSAccess) {
        setActiveAppModule("HRMS");
        localStorage.setItem("activeAppModule", "HRMS");
      }
    } else if (path !== "/") {
      // For any other logistic path
      if (activeAppModule !== "Logistic" && hasLogisticAccess) {
        setActiveAppModule("Logistic");
        localStorage.setItem("activeAppModule", "Logistic");
      }
    }
  }, [location.pathname, hasHRMSAccess, hasLogisticAccess, activeAppModule]);

  useEffect(() => {
    if (activeAppModule === "Logistic" && !hasLogisticAccess) {
      setActiveAppModule("HRMS");
      localStorage.setItem("activeAppModule", "HRMS");
    } else if (activeAppModule === "HRMS" && !hasHRMSAccess) {
      setActiveAppModule("Logistic");
      localStorage.setItem("activeAppModule", "Logistic");
    }
  }, [activeAppModule, hasLogisticAccess, hasHRMSAccess]);

  const filteredNavItems = useMemo(() => {
    const isSuperAdmin = profile?.type === "superAdmin" || profile?.role === "superAdmin";

    return navItems.filter((nav: any) => {
      if (nav.isHRMS && !hasHRMSAccess) {
        return false;
      }
      
      // Filter by active app module
      if (activeAppModule === "Logistic") {
        if (nav.isHRMS) return false;
      } else if (activeAppModule === "HRMS") {
        if (!nav.isHRMS) return false;
      }
      
      if (nav.name === "Dark Store") {
        return (
          profile?.clientType === "BOTH" ||
          profile?.clientType === "ALL" ||
          permissions?.some((p: any) => p?.module === "Dark Store" && p?.operations?.includes("read"))
        );
      }

      // Permission checks for Logistic / non-HRMS modules
      if (!nav.isHRMS && nav.modules && nav.modules.length > 0) {
        if (isSuperAdmin) return true;
        if (!permissions) return false;
        
        return nav.modules.some((mod: string) =>
          permissions.some((p: any) => p.module === mod && p.operations.includes("read"))
        );
      }
      
      return true;
    });
  }, [hasHRMSAccess, profile, permissions, activeAppModule]);
  const { allowed: canViewWarehouse } = usePermission("warehouse", "read");

  const [activeItem, setActiveItem] = useState(location.pathname);
  const [showSearch, setShowSearch] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [openResponse, setOpenResponse] = useState(false);
  const [bulkJobId, setBulkJobId] = useState<string>("");

  // Fetch client permissions automatically if not already fetched
  const { isLoading: isLoadingPermissions } = useClientPermissions();

  /**
   * Filter nav items by permission.
   * - If permissions haven't loaded yet or are loading → show empty array to avoid flashing unauthorized links.
   * - If a nav item has no `modules` array → always show (if any).
   * - Otherwise show only if the user has 'read' access to AT LEAST ONE of the listed modules.
   */
  const [notification, setNotification] = useState<any>(null);

  const visibleNavItems = useMemo(() => {
    return navItems;
  }, []);

  const notificationRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [searchFor, setSearchFor] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const [showAppsDropdown, setShowAppsDropdown] = useState(false);
  const appsDropdownRef = useRef<HTMLDivElement>(null);

  const handleAppModuleSelect = (module: string) => {
    if (module === "CRM") {
      toast("CRM module is coming soon!", { icon: "🚀" });
      return;
    }
    setActiveAppModule(module);
    localStorage.setItem("activeAppModule", module);
    setShowAppsDropdown(false);
    
    // Redirect to the first available tab for that module
    if (module === "HRMS") navigate("/company");
    else if (module === "Logistic") navigate("/orders");
  };
  const debouncedSearch = useDebounce(searchFor, 300);
  const { refetch: profileRefetch } = useProfileData();
  const url = process.env.REACT_APP_BASE_URL;

  const { language, setLanguage, t } = useLanguage();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const languageRef = useRef<HTMLDivElement>(null);

  /*
  // Load Google Translate script and initialize hidden element
  useEffect(() => {
    (window as any).googleTranslateElementInit = () => {
      if ((window as any).google && (window as any).google.translate) {
        new (window as any).google.translate.TranslateElement(
          {
            pageLanguage: "en",
            includedLanguages: "en,ar",
            layout: (window as any).google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false,
          },
          "google_translate_element"
        );
      }
    };

    if (!document.getElementById("google-translate-script")) {
      const script = document.createElement("script");
      script.id = "google-translate-script";
      script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);
    } else {
      if ((window as any).googleTranslateElementInit) {
        (window as any).googleTranslateElementInit();
      }
    }
  }, []);

  const clearGoogleTranslateCookies = useCallback(() => {
    const hostname = window.location.hostname;
    const domainParts = hostname.split(".");
    const mainDomain = domainParts.length > 1 ? domainParts.slice(-2).join(".") : "";
    const domains = [
      "",
      hostname,
      `.${hostname}`,
      mainDomain,
      `.${mainDomain}`
    ].filter(Boolean);

    const paths = ["/", "/index.html"];

    domains.forEach((d) => {
      paths.forEach((p) => {
        document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${p}; domain=${d};`;
        document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${p};`;
      });
    });
  }, []);

  const triggerGoogleTranslate = useCallback((lang: "en" | "ar", isUserAction = false) => {
    if (lang === "en") {
      clearGoogleTranslateCookies();
      const selectEl = document.querySelector(".goog-te-combo") as HTMLSelectElement;
      if (selectEl) {
        selectEl.value = "en";
        const event = new Event("change", { bubbles: true });
        selectEl.dispatchEvent(event);
      }
      if (isUserAction) {
        window.location.reload();
      }
      return;
    }

    const cookieName = "googtrans";
    const hostname = window.location.hostname;
    
    // Clear old conflicting cookies first
    clearGoogleTranslateCookies();

    // Set new cookie on path=/
    document.cookie = `${cookieName}=/en/${lang}; path=/;`;
    
    const domainParts = hostname.split(".");
    const isIP = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname);
    if (!isIP && domainParts.length > 1) {
      const mainDomain = domainParts.slice(-2).join(".");
      document.cookie = `${cookieName}=/en/${lang}; path=/; domain=.${mainDomain};`;
    }

    const selectEl = document.querySelector(".goog-te-combo") as HTMLSelectElement;
    if (selectEl) {
      selectEl.value = lang;
      const event = new Event("change", { bubbles: true });
      selectEl.dispatchEvent(event);
    } else {
      let retries = 0;
      const interval = setInterval(() => {
        const select = document.querySelector(".goog-te-combo") as HTMLSelectElement;
        if (select) {
          select.value = lang;
          const event = new Event("change", { bubbles: true });
          select.dispatchEvent(event);
          clearInterval(interval);
        } else {
          retries++;
          if (retries > 10) {
            clearInterval(interval);
            console.log("goog-te-combo not found.");
            if (isUserAction) {
              window.location.reload();
            }
          }
        }
      }, 300);
    }
  }, [clearGoogleTranslateCookies]);

  // Sync language with profile preferredLanguage on load
  useEffect(() => {
    if (profile?.preferredLanguage) {
      const prefLang = profile.preferredLanguage as "en" | "ar";
      setCurrentLanguage(prefLang);

      const getCookie = (name: string) => {
        const val = `; ${document.cookie}`;
        const parts = val.split(`; ${name}=`);
        if (parts.length >= 2) return parts.pop()?.split(";").shift();
      };

      const currentCookie = getCookie("googtrans");

      if (prefLang === "en") {
        const isTranslated =
          document.documentElement.classList.contains("translated-ltr") ||
          document.documentElement.classList.contains("translated-rtl") ||
          (document.documentElement.lang && document.documentElement.lang !== "en");

        if (currentCookie || isTranslated) {
          const timer = setTimeout(() => {
            triggerGoogleTranslate("en", false);
          }, 1000);
          return () => clearTimeout(timer);
        }
      } else {
        const expectedCookie = `/en/ar`;
        const isTranslated =
          document.documentElement.lang === "ar" ||
          document.documentElement.classList.contains("translated-rtl");

        if (currentCookie !== expectedCookie || !isTranslated) {
          const timer = setTimeout(() => {
            triggerGoogleTranslate("ar", false);
          }, 1000);
          return () => clearTimeout(timer);
        }
      }
    }
  }, [profile?.preferredLanguage, triggerGoogleTranslate]);

  const handleLanguageChange = async (lang: "en" | "ar") => {
    setCurrentLanguage(lang);
    setShowLanguageDropdown(false);
    triggerGoogleTranslate(lang, true);

    try {
      await fetch5(`${url}/api/auth/language`, "PUT", { language: lang });
      profileRefetch();
      if (typeof refetch === "function") {
        refetch();
      }
    } catch (err) {
      console.error("Failed to save language preference:", err);
    }
  };
  */

  const LogoComponent =
    process.env.REACT_APP_EXTERNAL_ADMIN === "true"
      ? "/logo/logo-black.png"
      : "/img/dashboard-icons/logo-tracktech.svg";

  useEffect(() => setActiveItem(location.pathname), [location.pathname]);

  const handleNavClick = useCallback(
    (path: string, e?: React.MouseEvent) => {
      if (e && (e.ctrlKey || e.metaKey)) {
        window.open(path, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(path);
      setActiveItem(path);
      setShowSidebar(false);
    },
    [navigate],
  );

  const searchGlobalApi = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setIsSearching(true);
      setShowSearchResults(true);

      try {
        const res: any = await fetch3(
          `${url}/api/search/global?query=${encodeURIComponent(query)}`,
          "get",
        );

        const mapped = res.data.map((i: any) => ({
          ...i,
          subText: i.orderName
            ? `Order: ${i.orderName} | Status: ${i.status}`
            : i.subText,
        }));
        setSearchResults(mapped);
      } catch (err) {
        console.error(err);
        toast.error("Failed to fetch search results.");
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [url],
  );

  useEffect(() => {
    searchGlobalApi(debouncedSearch);
  }, [debouncedSearch, searchGlobalApi]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target as Node)
      ) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchFor.trim().length >= 2) {
      setShowSearchResults(false);
      setShowSearch(false);
      navigate("/search-results", {
        state: { searchResults, searchQuery: searchFor },
      });
      setSearchFor("");
      setSearchResults([]);
    }
  };

  const handleSearchResultClick = (item: SearchResultItem) => {
    setShowSearchResults(false);
    setShowSearch(false);
    navigate(getEntityPath(item.entityType, item.entityId));
  };

  const { data, refetch } = useProfileData();
  const { mutate } = useUpdateProfilePicMutation();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    mutate(
      { companyLogo: file },
      {
        onSuccess: () => {
          toast.success("Profile picture Updated!");
          refetch();
        },
        onError: (err: any) => {
          toast.error(err?.message ?? "Upload failed.");
        },
      },
    );
  };

  const handleLogout = async () => {
    await fetch3(`${url}/api/auth/logout?type=client`, "get");
    if (typeof window !== "undefined") {
      window.localStorage.removeItem("auth");
    }
    queryClient.clear();
    refetch();
    navigate("/");
  };

  const socket = useMemo(() => {
    const s = io(url!, { transports: ["websocket"] });
    s.on("notification Sent", (payload) => {
      if (payload.recipientId === profile?.id) {
        toast.success(payload.message ?? "New notification");
      }
    });
    return s;
  }, [url, profile?.id]);

  const { data: notifications, refetch: fetchNotifications } =
    useFetchAdminNotificationsData(1, 10, "");

  useEffect(() => {
    const closeNoti = (e: MouseEvent) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(e.target as Node)
      )
        setShowNotification(false);
    };
    document.addEventListener("mousedown", closeNoti);
    return () => document.removeEventListener("mousedown", closeNoti);
  }, []);

  useEffect(() => {
    const closeProf = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node))
        setShowProfileDropdown(false);
    };
    document.addEventListener("mousedown", closeProf);
    return () => document.removeEventListener("mousedown", closeProf);
  }, []);

  useEffect(() => {
    const closeLang = (e: MouseEvent) => {
      if (
        languageRef.current &&
        !languageRef.current.contains(e.target as Node)
      )
        setShowLanguageDropdown(false);
    };
    document.addEventListener("mousedown", closeLang);
    return () => document.removeEventListener("mousedown", closeLang);
  }, []);

  const [isIpadPro, setIsIpadPro] = useState(false);

  useEffect(() => {
    const ipadProQuery = window.matchMedia(
      "(min-device-width: 1024px) and (max-device-width: 1024px) and (min-device-height: 1366px) and (max-device-height: 1366px)",
    );

    setIsIpadPro(ipadProQuery.matches);

    const listener = (e: any) => setIsIpadPro(e.matches);
    ipadProQuery.addEventListener("change", listener);

    return () => ipadProQuery.removeEventListener("change", listener);
  }, []);

  return (
    <>
      {isIpadPro ? (
        <div className="p-4 sticky top-0 z-[40]">
          <header className="flex items-center justify-between px-6 py-2 bg-[#F7F7F5]/50 backdrop-blur-md rounded-full shadow border border-white">
            <div
              onClick={() => navigate("/orders")}
              className="flex items-end gap-2 cursor-pointer"
            >
              <img src={LogoComponent} alt="Logo" className="max-w-[50px]" />
              <div
                title={t(profile?.type === "lmStaff" ? profile.roles : "Admin")}
                className="px-2 py-1 text-xs font-medium text-black uppercase border border-[#212121] rounded-full whitespace-nowrap"
              >
                {profile?.type === "lmStaff" ? t(profile.roles) : t("Admin")}
              </div>
            </div>

            {/* Desktop Nav */}
            <nav
              className={`flex items-center flex-nowrap space-x-0 transition-all duration-300 bg-[#F7F7F5] rounded-full overflow-x-auto hide-scrollbar max-w-[calc(100vw-320px)] ${
                showSearch ? "w-0 overflow-hidden" : "w-auto p-1"
              }`}
            >
              {filteredNavItems
                ?.map((item) => (
                  <a
                    key={item.path}
                    href={item.path}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(item.path, e);
                    }}
                    className={`flex items-center cursor-pointer px-3 py-2.5 rounded-full whitespace-nowrap flex-shrink-0 ${
                      activeItem === item.path
                        ? "bg-[#303030] text-white"
                        : "bg-transparent text-[#212121]"
                    }`}
                  >
                    <p
                      className={`text-xs font-medium ${
                        activeItem === item.path ? "text-white" : "text-[#212121]"
                    }`}
                  >
                    <T>{item.name}</T>
                  </p>
                </a>
              ))}
            </nav>

            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <div className="relative" ref={searchContainerRef}>
                <div
                  className={`flex items-center transition-all duration-300 ease-in-out overflow-hidden border border-[#FFFFFF]/20 rounded-full cursor-pointer h-10 shadow-sm ${
                    showSearch ? "w-96 bg-[#F7F7F5]" : "w-10 bg-transparent"
                  }`}
                >
                  <div
                    className="flex items-center justify-center w-10 h-10 text-[#212121] bg-[#F7F7F5] flex-shrink-0"
                    onClick={() => !showSearch && setShowSearch(true)}
                  >
                    <IconSearch strokeWidth={1.5} size={24} />
                  </div>

                  {showSearch && (
                    <>
                      <input
                        type="text"
                        placeholder={t("search awb, order...")}
                        autoFocus
                        value={searchFor}
                        onChange={(e) => setSearchFor(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        className="flex-1 h-full px-2 text-sm text-[#212121] placeholder-[#212121]/70 bg-transparent border-none outline-none"
                      />
                      <div
                        className="flex items-center justify-center w-12 h-12 text-[#212121] flex-shrink-0"
                        onClick={() => {
                          setShowSearch(false);
                          setSearchFor("");
                          setSearchResults([]);
                        }}
                      >
                        <IconX className="w-5 h-5" />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div ref={appsDropdownRef} className="relative">
                <div
                  className="relative flex items-center gap-2 px-3 h-10 border border-[#D2D2D2] rounded-full cursor-pointer bg-white hover:bg-gray-50 transition shadow-sm"
                  onClick={() => setShowAppsDropdown((p) => !p)}
                >
                  <span className="text-sm font-semibold text-[#212121]">Apps</span>
                  <IconGridDots strokeWidth={1.5} size={20} className="text-[#212121]" />
                </div>

                {showAppsDropdown && (
                  <div className="absolute right-0 z-[1000] w-[340px] p-6 mt-2 bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100">
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center text-white">
                          <IconGridDots size={24} stroke={2} />
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-[#212121]">Applications</h3>
                          <p className="text-xs text-gray-500">Choose your workspace</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => setShowAppsDropdown(false)}
                        className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition"
                      >
                        <IconX size={14} stroke={2} />
                      </button>
                    </div>

                    <div className="grid grid-cols-3 gap-y-4 gap-x-2 mt-4">
                      {hasLogisticAccess && (
                        <div 
                          onClick={() => handleAppModuleSelect("Logistic")}
                          className={`flex flex-col items-center gap-2 cursor-pointer group p-3 rounded-2xl transition-colors ${activeAppModule === "Logistic" ? "bg-[#E8F0FE]" : "hover:bg-gray-50"}`}
                        >
                          <div className="w-12 h-12 rounded-[16px] flex items-center justify-center transition-transform group-hover:scale-105 shadow-sm bg-green-600 text-white">
                            <IconPackage size={24} stroke={1.5} />
                          </div>
                          <span className="text-xs font-semibold text-[#212121]">Logistic</span>
                        </div>
                      )}

                      {hasHRMSAccess && (
                        <div 
                          onClick={() => handleAppModuleSelect("HRMS")}
                          className={`flex flex-col items-center gap-2 cursor-pointer group p-3 rounded-2xl transition-colors ${activeAppModule === "HRMS" ? "bg-[#E8F0FE]" : "hover:bg-gray-50"}`}
                        >
                          <div className="w-12 h-12 rounded-[16px] flex items-center justify-center transition-transform group-hover:scale-105 shadow-sm bg-[#FBD037] text-[#212121]">
                            <IconUsersGroup size={24} stroke={1.5} />
                          </div>
                          <span className="text-xs font-semibold text-[#212121]">HRMS</span>
                        </div>
                      )}



                      <div 
                        onClick={() => handleAppModuleSelect("CRM")}
                        className={`flex flex-col items-center gap-2 cursor-pointer group p-3 rounded-2xl transition-colors hover:bg-gray-50`}
                      >
                        <div className="w-12 h-12 rounded-[16px] flex items-center justify-center bg-purple-500 text-white transition-transform group-hover:scale-105 shadow-sm">
                          <IconHeadset size={24} stroke={1.5} />
                        </div>
                        <span className="text-xs font-semibold text-[#212121]">CRM</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div ref={notificationRef}>
                <div
                  className="relative flex items-center justify-center w-10 h-10 border border-[#FFFFFF]/20 rounded-full cursor-pointer bg-[#F7F7F5]"
                  onClick={() => {
                    fetchNotifications();
                    setShowNotification((p) => !p);
                  }}
                >
                  <IconBell
                    strokeWidth={1.5}
                    size={24}
                    className="text-[#212121]"
                  />
                  {profile?.unseenNotification ? (
                    <span className="absolute top-0 end-0 flex items-center justify-center w-5 h-5 text-xs text-white bg-red-500 rounded-full">
                      {profile.unseenNotification}
                    </span>
                  ) : null}
                </div>

                {showNotification && (
                  <div
                    className={`absolute ${language === "ar" ? "left-0" : "right-0"} z-[1000] w-96 p-4 mt-2 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#FFFFFF]/10 rounded-xl shadow-lg`}
                  >
                    <h3 className="mb-2 text-lg">{t("Notifications")}</h3>
                    <div className="max-h-96   overflow-y-auto">
                      {notifications?.notifications?.length ? (
                        notifications.notifications
                          .slice(0, 5)
                          .map((n: any) => (
                            <NotificationItem
                              key={n.id}
                              n={n}
                              onClick={() => {
                                if (n.title?.includes("Bulk Job Completed")) {
                                  setBulkJobId(n.referenceId);
                                  setNotification(n);
                                  setOpenResponse(true);
                                } else if (
                                  n.notification_for?.startsWith("Chat")
                                ) {
                                  navigate(
                                    `/help-and-support?chatId=${n.referenceId}`,
                                  );
                                } else {
                                  navigate("/notification-view");
                                }
                                setShowNotification(false);
                              }}
                            />
                          ))
                      ) : (
                        <p className="py-6 text-sm text-center text-gray-500">
                          {t("No notifications")}
                        </p>
                      )}
                    </div>

                    <div className="pt-2 text-center border-t border-[#FFFFFF]/20">
                      <button
                        onClick={() => {
                          navigate("/notification-view");
                          setShowNotification(false);
                        }}
                        className="text-sm font-medium text-[#212121]/70 hover:text-[#FBD037] hover:underline"
                      >
                        {t("Show More")}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Language Switcher */}
              <div className="relative" ref={languageRef}>
                <div
                  className="flex items-center justify-center w-10 h-10 border border-[#FFFFFF]/20 rounded-full cursor-pointer bg-[#F7F7F5] transition-all hover:scale-105 duration-200"
                  onClick={() => setShowLanguageDropdown((p) => !p)}
                  title="Change Language / تغيير اللغة"
                >
                  <IconLanguage
                    strokeWidth={1.5}
                    size={24}
                    className="text-[#212121]"
                  />
                </div>

                {showLanguageDropdown && (
                  <div
                    className={`absolute ${language === "ar" ? "left-0" : "right-0"} z-[1000] w-44 p-2 mt-2 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#E5E7EB] rounded-xl shadow-lg`}
                  >
                    <ul className="space-y-1 text-sm text-[#212121]">
                      <li
                        className={`px-3 py-2 rounded-lg cursor-pointer hover:bg-[#F7F6F2] transition flex justify-between items-center ${language === "en" ? "font-bold text-[#FBD037]" : ""}`}
                        onClick={() => {
                          setLanguage("en");
                          setShowLanguageDropdown(false);
                        }}
                      >
                        <span>English</span>
                        {language === "en" && (
                          <span className="w-2 h-2 rounded-full bg-[#FBD037]" />
                        )}
                      </li>
                      <li
                        className={`px-3 py-2 rounded-lg cursor-pointer hover:bg-[#F7F6F2] transition flex justify-between items-center ${language === "ar" ? "font-bold text-[#FBD037]" : ""}`}
                        onClick={() => {
                          setLanguage("ar");
                          setShowLanguageDropdown(false);
                        }}
                      >
                        <span>العربية</span>
                        {language === "ar" && (
                          <span className="w-2 h-2 rounded-full bg-[#FBD037]" />
                        )}
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              <div className="relative" ref={profileRef}>
                <div
                  className="flex items-center justify-center w-10 h-10 overflow-hidden rounded-full cursor-pointer"
                  onClick={() => setShowProfileDropdown((p) => !p)}
                >
                  <img
                    className="object-cover w-10 h-10 rounded-full"
                    src={data?.logo ?? "/img/dumy-profile.png"}
                    alt="Avatar"
                  />
                </div>

                {showProfileDropdown && (
                  <div
                    className={`absolute ${language === "ar" ? "left-0" : "right-0"} z-[1000] w-72 p-0 mt-2 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#FFFFFF]/10 rounded-xl shadow-lg`}
                  >
                    <div className="flex items-center gap-2 px-4 py-4 border-b border-[#E5E7EB]">
                      <div className="relative group">
                        <img
                          className="w-14 h-14 object-cover rounded-full"
                          src={data?.logo ?? "/img/dumy-profile.png"}
                          alt="Avatar"
                        />
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 cursor-pointer group-hover:opacity-100 transition"
                        >
                          <IconPencil size={24} color="white" />
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          ref={fileInputRef}
                          className="hidden"
                          onChange={handleFileChange}
                        />
                      </div>
                      <div>
                        <h2 className="text-sm font-medium text-[#212121]">
                          {data?.username}
                        </h2>
                        <p className="text-xs text-[#212121]/70">
                          {data?.email}
                        </p>
                      </div>
                    </div>

                    <ul className="py-2 space-y-2 text-sm text-[#212121]">
                      {[
                        {
                          icon: IconUser,
                          label: "Company Info",
                          path: "/profile",
                        },
                        {
                          icon: IconBasketCog,
                          label: "Shipping Config",
                          path: "/shipping-configuration",
                        },
                        ...(canViewWarehouse
                          ? [
                              {
                                icon: IconBuilding,
                                label: "Warehouse",
                                path: "/warehouse",
                              },
                            ]
                          : []),
                        {
                          icon: IconClipboardList,
                          label: "Subscription",
                          path: "/subscription",
                        },
                        {
                          icon: IconSettings,
                          label: "Settings",
                          path: "/settings",
                        },
                        {
                          icon: IconSettings,
                          label: "Setting HRMS Config",
                          path: "/hr-settings",
                        },
                        {
                          icon: IconHistory,
                          label: "Activity Logs",
                          path: "/activity-logs",
                        },
                        {
                          icon: IconHeadset,
                          label: "Help & Support",
                          path: "/help-and-support",
                        },
                      ].map((i) => (
                        <li
                          key={i.path}
                          className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-[#F7F6F2]"
                          onClick={() => {
                            navigate(i.path);
                            setShowProfileDropdown(false);
                          }}
                        >
                          <div className="flex items-center gap-1">
                            <i.icon stroke={1.5} />
                            <T>{i.label}</T>
                          </div>
                          <IconChevronRight
                            size={22}
                            stroke={1.5}
                            color="#6B7280"
                          />
                        </li>
                      ))}

                      <li
                        className="flex items-center gap-1 px-4 py-2 cursor-pointer hover:bg-[#F7F6F2]"
                        onClick={() => {
                          handleLogout();
                          setShowProfileDropdown(false);
                        }}
                      >
                        <IconLogout size={22} stroke={1.5} /> <T>Logout</T>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </header>
        </div>
      ) : (
        <div className="sticky top-0 z-[40]">
          <div className="p-2 sm:p-4">
            <header className="flex items-center justify-between px-3 sm:px-6 py-1.5 sm:py-2 bg-[#F7F7F5]/50 backdrop-blur-md rounded-full shadow border border-white">
              <div
                onClick={() => navigate("/orders")}
                className="flex items-end gap-1.5 sm:gap-2 cursor-pointer"
              >
                <img
                  src={LogoComponent}
                  alt="Logo"
                  className="max-w-[100px] xs:max-w-[120px] sm:max-w-[130px]"
                />
                <div
                  title={t(
                    profile?.type === "lmStaff" ? profile.roles : "Admin",
                  )}
                  className="px-2 py-0.5 sm:px-4 sm:py-1 text-[10px] sm:text-xs font-medium text-black uppercase border border-[#212121] rounded-full whitespace-nowrap"
                >
                  {profile?.type === "lmStaff"
                    ? profile.roles
                        ?.split(" ")
                        .map((w: string) => w[0].toUpperCase())
                        .join("")
                    : t("Admin")}
                </div>
              </div>

              {/* Desktop Nav */}
              <nav
                className={`hidden xl:flex items-center flex-nowrap space-x-0 transition-all duration-300 bg-[#F7F7F5] rounded-full overflow-x-auto hide-scrollbar max-w-[calc(100vw-300px)] ${
                  showSearch ? "w-0 overflow-hidden" : "w-auto p-1"
                }`}
              >
                {filteredNavItems
                  ?.map((item) => (
                    <a
                      key={item.path}
                      href={item.path}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavClick(item.path, e);
                      }}
                      className={`flex items-center cursor-pointer px-3 py-1.5 rounded-full whitespace-nowrap flex-shrink-0 ${
                        activeItem === item.path
                          ? "bg-[#303030] text-white"
                          : "bg-transparent text-[#212121]"
                      }`}
                    >
                      <p
                        className={`text-[13px] font-medium ${
                          activeItem === item.path
                            ? "text-white"
                            : "text-[#212121]"
                        }`}
                      >
                        <T>{item.name}</T>
                      </p>
                    </a>
                  ))}
              </nav>

              <div className="flex items-center space-x-1.5 sm:space-x-3 rtl:space-x-reverse">
                {/* Search */}
                <div className="relative" ref={searchContainerRef}>
                  <div
                    className={`flex items-center transition-all duration-300 ease-in-out overflow-hidden border border-[#FFFFFF]/20 rounded-full cursor-pointer h-9 sm:h-12 shadow-sm ${
                      showSearch
                        ? "w-64 sm:w-96 bg-[#F7F7F5]"
                        : "w-9 sm:w-12 bg-transparent"
                    }`}
                  >
                    <div
                      className="flex items-center justify-center w-9 h-9 sm:w-12 sm:h-12 text-[#212121] bg-[#F7F7F5] flex-shrink-0"
                      onClick={() => !showSearch && setShowSearch(true)}
                    >
                      <IconSearch
                        strokeWidth={1.5}
                        size={20}
                        className="sm:hidden"
                      />
                      <IconSearch
                        strokeWidth={1.5}
                        size={24}
                        className="hidden sm:block"
                      />
                    </div>

                    {showSearch && (
                      <>
                        <input
                          type="text"
                          placeholder={t("search awb, order...")}
                          autoFocus
                          value={searchFor}
                          onChange={(e) => setSearchFor(e.target.value)}
                          onKeyDown={handleSearchKeyDown}
                          className="flex-1 h-full px-2 text-xs sm:text-sm text-[#212121] placeholder-[#212121]/70 bg-transparent border-none outline-none focus:outline-none focus:ring-0"
                        />
                        <div
                          className="flex items-center justify-center w-9 h-9 sm:w-12 sm:h-12 text-[#212121] flex-shrink-0"
                          onClick={() => {
                            setShowSearch(false);
                            setSearchFor("");
                            setSearchResults([]);
                          }}
                        >
                          <IconX className="w-4 h-4 sm:w-5 sm:h-5" />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div ref={appsDropdownRef} className="relative">
                  <div
                    className="relative flex items-center gap-2 px-4 h-12 border border-[#D2D2D2] rounded-full cursor-pointer bg-white hover:bg-gray-50 transition shadow-sm"
                    onClick={() => setShowAppsDropdown((p) => !p)}
                  >
                    <span className="text-sm font-semibold text-[#212121]">Apps</span>
                    <IconGridDots strokeWidth={1.5} size={20} className="text-[#212121]" />
                  </div>

                  {showAppsDropdown && (
                    <div className="absolute right-0 z-[1000] w-[340px] p-6 mt-2 bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100">
                      <div className="flex justify-between items-start mb-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#FF6B00] rounded-xl flex items-center justify-center text-white shadow-sm">
                            <IconGridDots size={24} stroke={2} />
                          </div>
                          <div>
                            <h3 className="text-base font-bold text-[#212121]">Applications</h3>
                            <p className="text-xs text-gray-500">Choose your workspace</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => setShowAppsDropdown(false)}
                          className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition"
                        >
                          <IconX size={14} stroke={2} />
                        </button>
                      </div>

                      <div className="grid grid-cols-3 gap-y-4 gap-x-2 mt-4">
                        {hasLogisticAccess && (
                          <div 
                            onClick={() => handleAppModuleSelect("Logistic")}
                            className={`flex flex-col items-center gap-2 cursor-pointer group p-3 rounded-2xl transition-colors ${activeAppModule === "Logistic" ? "bg-[#E8F0FE]" : "hover:bg-gray-50"}`}
                          >
                            <div className="w-12 h-12 rounded-[16px] flex items-center justify-center transition-transform group-hover:scale-105 shadow-sm bg-green-600 text-white">
                              <IconPackage size={24} stroke={1.5} />
                            </div>
                            <span className="text-xs font-semibold text-[#212121]">Logistic</span>
                          </div>
                        )}

                        {hasHRMSAccess && (
                          <div 
                            onClick={() => handleAppModuleSelect("HRMS")}
                            className={`flex flex-col items-center gap-2 cursor-pointer group p-3 rounded-2xl transition-colors ${activeAppModule === "HRMS" ? "bg-[#E8F0FE]" : "hover:bg-gray-50"}`}
                          >
                            <div className="w-12 h-12 rounded-[16px] flex items-center justify-center transition-transform group-hover:scale-105 shadow-sm bg-[#FBD037] text-[#212121]">
                              <IconUsersGroup size={24} stroke={1.5} />
                            </div>
                            <span className="text-xs font-semibold text-[#212121]">HRMS</span>
                          </div>
                        )}



                        <div 
                          onClick={() => handleAppModuleSelect("CRM")}
                          className={`flex flex-col items-center gap-2 cursor-pointer group p-3 rounded-2xl transition-colors hover:bg-gray-50`}
                        >
                          <div className="w-12 h-12 rounded-[16px] flex items-center justify-center bg-purple-500 text-white transition-transform group-hover:scale-105 shadow-sm">
                            <IconHeadset size={24} stroke={1.5} />
                          </div>
                          <span className="text-xs font-semibold text-[#212121]">CRM</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Notifications */}
                <div ref={notificationRef}>
                  <div
                    className="relative flex items-center justify-center w-9 h-9 sm:w-12 sm:h-12 border border-[#FFFFFF]/20 rounded-full cursor-pointer bg-[#F7F7F5]"
                    onClick={() => {
                      fetchNotifications();
                      setShowNotification((p) => !p);
                    }}
                  >
                    <IconBell
                      strokeWidth={1.5}
                      size={20}
                      className="text-[#212121] sm:hidden"
                    />
                    <IconBell
                      strokeWidth={1.5}
                      size={24}
                      className="text-[#212121] hidden sm:block"
                    />
                    {profile?.unseenNotification ? (
                      <span className="absolute top-0 end-0 flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 text-[9px] sm:text-xs text-white bg-red-500 rounded-full">
                        {profile.unseenNotification}
                      </span>
                    ) : null}
                  </div>

                  {showNotification && (
                    <div
                      className={`absolute ${language === "ar" ? "left-0" : "right-0"} z-[1000] w-72 sm:w-96 p-4 mt-2 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#FFFFFF]/10 rounded-xl shadow-lg`}
                    >
                      <h3 className="mb-2 text-base sm:text-lg">
                        {t("Notifications")}
                      </h3>
                      <div className="max-h-96 overflow-y-auto">
                        {notifications?.notifications?.length ? (
                          notifications.notifications
                            .slice(0, 5)
                            .map((n: any) => (
                              <NotificationItem
                                key={n.id}
                                n={n}
                                onClick={() => {
                                  if (n.title?.includes("Bulk Job Completed")) {
                                    setBulkJobId(n.referenceId);
                                    setNotification(n);
                                    setOpenResponse(true);
                                  } else if (
                                    n.notification_for?.startsWith("Chat")
                                  ) {
                                    navigate(
                                      `/help-and-support?chatId=${n.referenceId}`,
                                    );
                                  } else {
                                    navigate("/notification-view");
                                  }
                                  setShowNotification(false);
                                }}
                              />
                            ))
                        ) : (
                          <p className="py-6 text-sm text-center text-gray-500">
                            {t("No notifications")}
                          </p>
                        )}
                      </div>

                      <div className="pt-2 text-center border-t border-[#FFFFFF]/20">
                        <button
                          onClick={() => {
                            navigate("/notification-view");
                            setShowNotification(false);
                          }}
                          className="text-xs sm:text-sm font-medium text-[#212121]/70 hover:text-[#FBD037] hover:underline"
                        >
                          {t("Show More")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Language Switcher */}
                <div className="relative" ref={languageRef}>
                  <div
                    className="flex items-center justify-center w-9 h-9 sm:w-12 sm:h-12 border border-[#FFFFFF]/20 rounded-full cursor-pointer bg-[#F7F7F5] transition-all hover:scale-105 duration-200"
                    onClick={() => setShowLanguageDropdown((p) => !p)}
                    title="Change Language / تغيير اللغة"
                  >
                    <IconLanguage
                      strokeWidth={1.5}
                      size={20}
                      className="text-[#212121] sm:hidden"
                    />
                    <IconLanguage
                      strokeWidth={1.5}
                      size={24}
                      className="text-[#212121] hidden sm:block"
                    />
                  </div>

                  {showLanguageDropdown && (
                    <div
                      className={`absolute ${language === "ar" ? "left-0" : "right-0"} z-[1000] w-40 sm:w-44 p-2 mt-2 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#E5E7EB] rounded-xl shadow-lg`}
                    >
                      <ul className="space-y-1 text-sm text-[#212121]">
                        <li
                          className={`px-3 py-2 rounded-lg cursor-pointer hover:bg-[#F7F6F2] transition flex justify-between items-center ${language === "en" ? "font-bold text-[#FBD037]" : ""}`}
                          onClick={() => {
                            setLanguage("en");
                            setShowLanguageDropdown(false);
                          }}
                        >
                          <span>English</span>
                          {language === "en" && (
                            <span className="w-2 h-2 rounded-full bg-[#FBD037]" />
                          )}
                        </li>
                        <li
                          className={`px-3 py-2 rounded-lg cursor-pointer hover:bg-[#F7F6F2] transition flex justify-between items-center ${language === "ar" ? "font-bold text-[#FBD037]" : ""}`}
                          onClick={() => {
                            setLanguage("ar");
                            setShowLanguageDropdown(false);
                          }}
                        >
                          <span>العربية</span>
                          {language === "ar" && (
                            <span className="w-2 h-2 rounded-full bg-[#FBD037]" />
                          )}
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Profile */}
                <div className="relative" ref={profileRef}>
                  <div
                    className="flex items-center justify-center w-9 h-9 sm:w-12 sm:h-12 overflow-hidden rounded-full cursor-pointer"
                    onClick={() => setShowProfileDropdown((p) => !p)}
                  >
                    <img
                      className="object-cover w-9 h-9 sm:w-12 sm:h-12 rounded-full"
                      src={data?.logo ?? "/img/dumy-profile.png"}
                      alt="Avatar"
                    />
                  </div>

                  {showProfileDropdown && (
                    <div
                      className={`absolute ${language === "ar" ? "left-0" : "right-0"} z-[1000] w-72 p-0 mt-2 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#FFFFFF]/10 rounded-xl shadow-lg`}
                    >
                      <div className="flex items-center gap-2 px-4 py-4 border-b border-[#E5E7EB]">
                        <div className="relative group">
                          <img
                            className="w-14 h-14 object-cover rounded-full"
                            src={data?.logo ?? "/img/dumy-profile.png"}
                            alt="Avatar"
                          />
                          <div
                            onClick={() => fileInputRef.current?.click()}
                            className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 cursor-pointer group-hover:opacity-100 transition"
                          >
                            <IconPencil size={24} color="white" />
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            className="hidden"
                            onChange={handleFileChange}
                          />
                        </div>
                        <div>
                          <h2 className="text-sm font-medium text-[#212121]">
                            {data?.username}
                          </h2>
                          <p className="text-xs text-[#212121]/70">
                            {data?.email}
                          </p>
                        </div>
                      </div>

                      <ul className="py-2 space-y-2 text-sm text-[#212121]">
                        {[
                          {
                            icon: IconUser,
                            label: "Company Info",
                            path: "/profile",
                          },
                          {
                            icon: IconBasketCog,
                            label: "Shipping Config",
                            path: "/shipping-configuration",
                          },
                          ...(canViewWarehouse
                            ? [
                                {
                                  icon: IconBuilding,
                                  label: "Warehouse",
                                  path: "/warehouse",
                                },
                              ]
                            : []),
                          {
                            icon: IconClipboardList,
                            label: "Subscription",
                            path: "/subscription",
                          },
                          {
                            icon: IconSettings,
                            label: "Settings",
                            path: "/settings",
                          },
                          {
                            icon: IconSettings,
                            label: "Setting HRMS Config",
                            path: "/hr-settings",
                          },
                          {
                            icon: IconHistory,
                            label: "Activity Logs",
                            path: "/activity-logs",
                          },
                          {
                            icon: IconHeadset,
                            label: "Help & Support",
                            path: "/help-and-support",
                          },
                        ].map((i) => (
                          <li
                            key={i.path}
                            className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-[#F7F6F2]"
                            onClick={() => {
                              navigate(i.path);
                              setShowProfileDropdown(false);
                            }}
                          >
                            <div className="flex items-center gap-1">
                              <i.icon stroke={1.5} />
                              <T>{i.label}</T>
                            </div>
                            <IconChevronRight
                              size={22}
                              stroke={1.5}
                              color="#6B7280"
                            />
                          </li>
                        ))}

                        <li
                          className="flex items-center gap-1 px-4 py-2 cursor-pointer hover:bg-[#F7F6F2]"
                          onClick={() => {
                            handleLogout();
                            setShowProfileDropdown(false);
                          }}
                        >
                          <IconLogout size={22} stroke={1.5} /> <T>Logout</T>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Hamburger Menu Icon */}
                <div
                  onClick={() => setShowSidebar((p) => !p)}
                  className="flex items-center justify-center w-9 h-9 sm:w-12 sm:h-12 border border-[#FFFFFF]/20 rounded-full cursor-pointer xl:hidden"
                >
                  <IconMenu2 className="w-5 h-5 sm:w-6 sm:h-6 text-[#212121]/70" />
                </div>
              </div>
            </header>
          </div>

          {showSidebar && (
            <>
              <div
                className="fixed inset-0 z-40 bg-black/30"
                onClick={() => setShowSidebar(false)}
              />
              <aside className="fixed top-0 end-0 z-50 w-64 h-full p-4 overflow-y-auto bg-[#1f1f1f] border-s border-[#FFFFFF]/10 shadow-2xl xl:hidden">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-white">
                    <T>Menu</T>
                  </h2>
                  <IconX
                    onClick={() => setShowSidebar(false)}
                    className="w-6 h-6 text-white cursor-pointer"
                  />
                </div>
                <nav className="flex flex-col space-y-2">
                  {filteredNavItems
                    ?.map((i) => (
                      <div
                        key={i.path}
                        onClick={() => handleNavClick(i.path)}
                        className={`px-4 py-3 text-sm rounded cursor-pointer flex items-center gap-2 ${
                          activeItem === i.path
                            ? "bg-[#FBD037] text-black"
                            : "text-white hover:bg-white/10"
                        }`}
                      >
                        {i.icon}
                        <T>{i.name}</T>
                      </div>
                    ))}
                </nav>
              </aside>
            </>
          )}

          {openResponse && (
            <BulkResponseHandler
              isOpen={openResponse}
              onClose={() => setOpenResponse(false)}
              bulkJobId={bulkJobId}
              notification={notification}
            />
          )}

          {/* <div id="google_translate_element" style={{ display: "none" }}></div> */}
        </div>
      )}
    </>
  );
}
